import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { nomenclature as nomenclatureApi } from '@/api/catalogs';
import {
  nomenclatureSpecifications,
  type NomenclatureSpecificationListItem,
} from '@/api/nomenclatureSpecifications';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'production.specification.manage';

export default function NomenclatureSpecificationsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['nomenclature-specifications', orgId],
    queryFn: () =>
      nomenclatureSpecifications.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const nomQ = useQuery({
    queryKey: ['nomenclature-all'],
    queryFn: () => nomenclatureApi.list({ limit: 1000 }),
  });
  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  const cols = useMemo<ColumnDef<NomenclatureSpecificationListItem>[]>(
    () => [
      {
        key: 'product_nomenclature_id',
        header: 'Продукція',
        sortValue: (r) => nomName.get(r.product_nomenclature_id) ?? '',
        cell: (r) => nomName.get(r.product_nomenclature_id) ?? '',
      },
      {
        key: 'name',
        header: 'Назва специфікації',
        sortValue: (r) => r.name ?? '',
        cell: (r) => r.name,
      },
      {
        key: 'base_quantity',
        header: 'Базова к-сть',
        width: 130,
        sortValue: (r) => Number(r.base_quantity),
        cell: (r) => r.base_quantity,
      },
      {
        key: 'is_active',
        header: 'Активна',
        width: 110,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
      {
        key: 'valid_from',
        header: 'Діє з',
        width: 160,
        sortValue: (r) => r.valid_from ?? '',
        cell: (r) => (r.valid_from ? new Date(r.valid_from).toLocaleDateString('uk-UA') : ''),
      },
    ],
    [nomName],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати специфікації у цій юрособі (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Специфікації</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/nomenclature-specifications/new')}
        >
          + Нова специфікація
        </button>
      </div>
      <DataTable<NomenclatureSpecificationListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає специфікацій."
        onRowDoubleClick={(r) => navigate(`/nomenclature-specifications/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="name"
      />
    </>
  );
}
